/**
 * The editor on phones (docs/planning/07-mobile-editor.md): a top bar (≡, the
 * open level, the resume, Play), the viewport filling the screen, and the
 * dock's panels in a sheet over it. The same panels, commands and tour as the
 * desktop editor; only the frame differs. What both share is the ShellFrame's.
 *
 * The viewport is sized to the part of the screen the sheet leaves uncovered
 * at its stop (not while it's dragged), so the camera, picking and overlays
 * work on exactly what's visible without knowing a sheet exists.
 */
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useEditor } from "../../EditorContext";
import { DownloadIcon } from "../../ui/icons";
import { ViewportPanel } from "../../viewport/ViewportPanel";
import type { PanelId } from "../layout";
import { OpenLevel } from "../MenuBar";
import { ShellFrame } from "../ShellFrame";
import type { MenuItems, TourOfferProp } from "../ShellFrame";
import { HistoryButtons, PlayButton } from "../Toolbar";
import { CompactMenu } from "./CompactMenu";
import { PanelSheet, sheetSize } from "./PanelSheet";
import type { Orientation } from "./PanelSheet";
import type { SheetStop } from "./sheetModel";
import "./compact.css";

/** The sheet's tabs, in order. Statistics joins them when Window › Statistics asks. */
const SHEET_TABS: readonly PanelId[] = ["outliner", "inspector", "contentBrowser", "console"];

export function CompactShell({ tourOffer = null }: TourOfferProp) {
  return (
    <ShellFrame tourOffer={tourOffer} className="kn-compact" layout="compact">
      {(menuItems) => <CompactLayout menuItems={menuItems} />}
    </ShellFrame>
  );
}

function CompactLayout({ menuItems }: { menuItems: MenuItems }) {
  const [stageRef, stage] = useElementSize<HTMLDivElement>();
  const sheet = useSheetState();
  // By the stage's shape, not the device's: a wide desktop window gets the side panel too.
  const orientation: Orientation = stage.width > stage.height ? "landscape" : "portrait";
  const available = orientation === "portrait" ? stage.height : stage.width;
  const inset = sheetSize(orientation, sheet.stop, available);

  return (
    <>
      <CompactTopBar menuItems={menuItems} />
      <div
        ref={stageRef}
        className="kn-compact__stage"
        data-orientation={orientation}
        style={{ "--kn-sheet-inset": `${inset}px` } as CSSProperties}
      >
        <div className="kn-compact__viewport">
          <ViewportPanel />
          {/* Undo and redo within thumb's reach, beside the two- and three-finger taps. */}
          <div className="kn-compact__history">
            <HistoryButtons />
          </div>
        </div>
        <PanelSheet
          orientation={orientation}
          available={available}
          stop={sheet.stop}
          tab={sheet.tab}
          tabs={sheet.tabs}
          onStop={sheet.setStop}
          onTab={sheet.openTab}
        />
      </div>
    </>
  );
}

function CompactTopBar({ menuItems }: { menuItems: MenuItems }) {
  const { links } = useEditor();

  return (
    <header className="kn-compact__topbar">
      <CompactMenu menuItems={menuItems} />
      <OpenLevel />
      <span className="kn-compact__spacer" />
      <a
        className="kn-compact__icon"
        href={links.resume}
        download
        aria-label="Download Resume.pdf"
        title="Download Resume.pdf"
      >
        <DownloadIcon size={18} />
      </a>
      <PlayButton />
    </header>
  );
}

/**
 * Which tab shows and where the sheet stops. `panel.focus` (the Console on `~`,
 * the tour, Window › Statistics) brings its panel forward, as the dock does,
 * and opens a peeking sheet so the panel can be seen.
 */
function useSheetState() {
  const { panelFocus } = useEditor();
  const [stop, setStop] = useState<SheetStop>("peek");
  const [tab, setTab] = useState<PanelId>("outliner");
  const [tabs, setTabs] = useState<readonly PanelId[]>(SHEET_TABS);

  const openTab = (id: PanelId) => {
    setTabs((current) => (current.includes(id) ? current : [...current, id]));
    setTab(id);
    setStop((current) => (current === "peek" ? "half" : current));
  };

  useEffect(
    () =>
      panelFocus.subscribe(() => {
        const id = panelFocus.getSnapshot().panel;
        // The viewport isn't in the sheet: it's always showing.
        if (id && id !== "viewport") openTab(id);
      }),
    [panelFocus],
  );

  return { stop, setStop, tab, tabs, openTab };
}

/** An element's size in CSS pixels, kept current as the layout changes. */
function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      setSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, size] as const;
}
